import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_BASE_URL from '../../api';
import Swal from 'sweetalert2';
import SlidingDrawer from '../../components/SlidingDrawer';

const ICONS = {
    search: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>,
    calendar: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>,
    authority: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path></svg>,
    noAgendas: <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /><polyline points="10 9 9 9 8 9" /></svg>
};

const AgendaManager = () => {
    const [agendas, setAgendas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('');
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [selectedAgenda, setSelectedAgenda] = useState(null);
    const [remark, setRemark] = useState('');

    useEffect(() => {
        fetchAgendas();
    }, []);

    const fetchAgendas = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`${API_BASE_URL}/api/secretariat/agendas/review`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setAgendas(res.data);
            setLoading(false);
        } catch (err) {
            console.error(err);
            setLoading(false);
        }
    };

    const handleAction = async (action) => {
        if (action === 'RETURN' && !remark.trim()) {
            Swal.fire('Required', 'Please add a remark when returning an agenda.', 'warning');
            return;
        }

        const confirmText = action === 'FORWARD'
            ? 'Forward to Speaker? This will lock the agenda.'
            : 'Return to Minister for corrections?';

        const result = await Swal.fire({
            title: 'Confirm Action',
            text: confirmText,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#C0722C',
            confirmButtonText: 'Yes, Proceed'
        });

        if (result.isConfirmed) {
            try {
                const token = localStorage.getItem('token');
                await axios.post(`${API_BASE_URL}/api/secretariat/agendas/${selectedAgenda._id}/review`, {
                    action: action,
                    remark: remark
                }, { headers: { Authorization: `Bearer ${token}` } });

                Swal.fire('Success', 'Agenda processed successfully', 'success');
                setIsDrawerOpen(false);
                setRemark('');
                fetchAgendas();
            } catch (err) {
                Swal.fire('Error', 'Failed to process agenda', 'error');
            }
        }
    };

    const getStatusStyles = (status) => {
        switch (status) {
            case 'SUBMITTED': return { bg: '#FFF3E0', text: '#E65100', dot: '#E65100' };
            case 'FORWARDED_TO_SPEAKER': return { bg: '#E8F5E9', text: '#2E7D32', dot: '#2E7D32' };
            case 'RETURNED': return { bg: '#FFEBEE', text: '#D32F2F', dot: '#D32F2F' };
            default: return { bg: '#F5F5F5', text: '#666', dot: '#666' };
        }
    };

    const filteredAgendas = agendas.filter(a =>
        a.title.toLowerCase().includes(filter.toLowerCase()) ||
        a.submittingAuthority.toLowerCase().includes(filter.toLowerCase()) ||
        (a.sessionId?.name || '').toLowerCase().includes(filter.toLowerCase())
    );

    return (
        <div style={{ maxWidth: '1400px', margin: '0 auto', fontFamily: '"Outfit", sans-serif' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '35px' }}>
                <div>
                    <h2 style={{ color: '#2C1A10', margin: '0 0 8px 0', fontSize: '32px', fontWeight: '800' }}>Agenda Review</h2>
                    <p style={{ margin: 0, color: '#888', fontSize: '16px' }}>Gatekeeper Protocol: Review, Return, or Forward Legislative Drafts.</p>
                </div>
                <div style={{ position: 'relative', width: '100%', maxWidth: '350px' }}>
                    <div style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#C0722C', display: 'flex', zIndex: 2 }}>
                        {ICONS.search}
                    </div>
                    <input
                        type="text"
                        placeholder="Search by Title, Authority or Session..."
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        style={{
                            width: '100%',
                            padding: '16px 20px 16px 48px',
                            borderRadius: '16px',
                            border: '1.5px solid #E5E5E5',
                            fontSize: '15px',
                            boxSizing: 'border-box',
                            outline: 'none',
                            transition: 'all 0.3s ease',
                            backgroundColor: '#fff',
                            color: '#333'
                        }}
                    />
                </div>
            </div>

            <div style={{ backgroundColor: 'white', borderRadius: '28px', boxShadow: '0 15px 45px rgba(0,0,0,0.03)', overflow: 'hidden', border: '1px solid #F0F0F0', minHeight: '500px' }}>
                <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0', textAlign: 'left' }}>
                    <thead style={{ backgroundColor: '#FDF8F3' }}>
                        <tr style={{ color: '#888', fontSize: '11px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '1.5px' }}>
                            <th style={{ padding: '25px 30px' }}>Agenda & Proponent</th>
                            <th style={{ padding: '25px 30px' }}>Jurisdiction</th>
                            <th style={{ padding: '25px 30px' }}>Session & Timeline</th>
                            <th style={{ padding: '25px 30px' }}>Workflow Status</th>
                            <th style={{ padding: '25px 30px', textAlign: 'right' }}>Directives</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan="5" style={{ padding: '80px', textAlign: 'center', color: '#888', fontWeight: '700' }}>SYNCHRONIZING REPOSITORY...</td></tr>
                        ) : filteredAgendas.length === 0 ? (
                            <tr>
                                <td colSpan="5" style={{ padding: '100px 40px', textAlign: 'center' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
                                        <div style={{
                                            width: '120px',
                                            height: '120px',
                                            backgroundColor: '#FDF8F3',
                                            borderRadius: '45px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: '#C0722C',
                                            marginBottom: '30px'
                                        }}>
                                            {ICONS.noAgendas}
                                        </div>
                                        <h3 style={{ color: '#2C1A10', margin: '0 0 12px 0', fontSize: '24px', fontWeight: '800' }}>Protocol Clear: No Agendas</h3>
                                        <p style={{ color: '#888', fontSize: '16px', margin: 0, maxWidth: '350px', lineHeight: '1.6' }}>
                                            {filter
                                                ? <span>The legislative review queue is currently empty for "<strong>{filter}</strong>".</span>
                                                : "The legislative review queue is currently clear and awaiting new ministerial submissions."
                                            }
                                        </p>
                                        <button
                                            onClick={() => setFilter('')}
                                            style={{
                                                marginTop: '35px',
                                                padding: '14px 28px',
                                                backgroundColor: 'transparent',
                                                border: '2px solid #C0722C',
                                                color: '#C0722C',
                                                borderRadius: '16px',
                                                fontWeight: '700',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            Reset Review Registry
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            filteredAgendas.map(agenda => {
                                const styles = getStatusStyles(agenda.status);
                                return (
                                    <tr key={agenda._id} style={{ borderBottom: '1px solid #F5F5F5', transition: 'background 0.2s' }}>
                                        <td style={{ padding: '25px 30px' }}>
                                            <div style={{ fontSize: '16px', fontWeight: '800', color: '#2C1A10' }}>{agenda.title}</div>
                                            <div style={{ fontSize: '12px', color: '#888', marginTop: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                <span style={{ display: 'flex' }}>{ICONS.authority}</span>
                                                {agenda.submittingAuthority}
                                            </div>
                                        </td>
                                        <td style={{ padding: '25px 30px' }}>
                                            <div style={{ fontSize: '14px', fontWeight: '700', color: '#444' }}>{agenda.department || 'General Administration'}</div>
                                            <div style={{ fontSize: '11px', color: '#999', marginTop: '4px' }}>Draft ID: #{agenda._id.slice(-6).toUpperCase()}</div>
                                        </td>
                                        <td style={{ padding: '25px 30px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#444', fontWeight: '600', fontSize: '14px' }}>
                                                <span style={{ color: '#C0722C' }}>{ICONS.calendar}</span>
                                                {agenda.sessionId?.name || 'General Session'}
                                            </div>
                                            <div style={{ fontSize: '12px', color: '#888', marginTop: '6px' }}>
                                                Submitted: {new Date(agenda.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                                            </div>
                                        </td>
                                        <td style={{ padding: '25px 30px' }}>
                                            <div style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                padding: '8px 14px',
                                                backgroundColor: styles.bg,
                                                borderRadius: '12px',
                                                border: `1px solid ${styles.text}20`
                                            }}>
                                                <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: styles.dot }}></div>
                                                <span style={{ color: styles.text, fontWeight: '800', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                    {agenda.status.replace(/_/g, ' ')}
                                                </span>
                                            </div>
                                        </td>
                                        <td style={{ padding: '25px 30px', textAlign: 'right' }}>
                                            <button
                                                onClick={() => { setSelectedAgenda(agenda); setIsDrawerOpen(true); }}
                                                style={{
                                                    padding: '12px 24px',
                                                    backgroundColor: '#2C1A10',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '12px',
                                                    fontWeight: '700',
                                                    fontSize: '13px',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.3s ease',
                                                    boxShadow: '0 4px 10px rgba(44, 26, 16, 0.15)'
                                                }}
                                                onMouseOver={(e) => e.target.style.backgroundColor = '#C0722C'}
                                                onMouseOut={(e) => e.target.style.backgroundColor = '#2C1A10'}
                                            >
                                                Review Action
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            <SlidingDrawer
                isOpen={isDrawerOpen}
                onClose={() => { setIsDrawerOpen(false); setRemark(''); }}
                title="Agenda Review Dossier"
            >
                {selectedAgenda && (
                    <div style={{ animation: 'fadeIn 0.4s ease' }}>
                        <div style={{ padding: '25px', backgroundColor: '#FDF8F3', borderRadius: '20px', border: '1px solid #F0E8DB', marginBottom: '30px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
                                <div>
                                    <div style={{ fontSize: '10px', fontWeight: '800', color: '#C0722C', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '8px' }}>Agenda Subject Matter</div>
                                    <div style={{ fontSize: '20px', fontWeight: '800', color: '#2C1A10', lineHeight: '1.4' }}>{selectedAgenda.title}</div>
                                </div>
                                <div style={{
                                    padding: '6px 12px',
                                    backgroundColor: getStatusStyles(selectedAgenda.status).bg,
                                    color: getStatusStyles(selectedAgenda.status).text,
                                    borderRadius: '10px',
                                    fontSize: '10px',
                                    fontWeight: '800',
                                    textTransform: 'uppercase'
                                }}>
                                    {selectedAgenda.status.replace(/_/g, ' ')}
                                </div>
                            </div>

                            <div style={{ marginBottom: '20px' }}>
                                <div style={{ fontSize: '10px', fontWeight: '800', color: '#C0722C', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '8px' }}>Agenda Narrative</div>
                                <div style={{ fontSize: '15px', color: '#444', lineHeight: '1.7', whiteSpace: 'pre-line' }}>{selectedAgenda.description}</div>
                            </div>

                            {selectedAgenda.remarks && selectedAgenda.remarks.length > 0 && (
                                <div style={{ marginBottom: '25px', padding: '20px', backgroundColor: '#FFF3E0', borderRadius: '16px', border: '1px solid #FFE0B2' }}>
                                    <div style={{ fontSize: '10px', fontWeight: '900', color: '#E65100', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
                                        Review History
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                                        {selectedAgenda.remarks.map((remark, idx) => (
                                            <div key={idx} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                                                <div style={{ minWidth: '4px', height: '4px', borderRadius: '50%', backgroundColor: '#E65100', marginTop: '8px' }}></div>
                                                <div>
                                                    <div style={{ fontSize: '14px', color: '#5D4037', fontWeight: '500', lineHeight: '1.5' }}>"{remark.text}"</div>
                                                    <div style={{ fontSize: '11px', color: '#E65100', marginTop: '4px', fontWeight: '700' }}>
                                                        {new Date(remark.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })} • {remark.by || 'Secretary'}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                                <div>
                                    <div style={{ fontSize: '10px', fontWeight: '800', color: '#C0722C', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '5px' }}>Submitting Proponent</div>
                                    <div style={{ fontSize: '14px', fontWeight: '700', color: '#2C1A10' }}>{selectedAgenda.submittingAuthority}</div>
                                </div>
                                <div>
                                    <div style={{ fontSize: '10px', fontWeight: '800', color: '#C0722C', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '5px' }}>Legislative Session</div>
                                    <div style={{ fontSize: '14px', fontWeight: '700', color: '#2C1A10' }}>{selectedAgenda.sessionId?.name || 'N/A'}</div>
                                </div>
                            </div>
                        </div>

                        {selectedAgenda.documents && selectedAgenda.documents.length > 0 && (
                            <div style={{ marginBottom: '30px' }}>
                                <div style={{ fontSize: '11px', fontWeight: '800', color: '#888', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '15px' }}>Supporting Documents</div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '10px' }}>
                                    {selectedAgenda.documents.map((doc, idx) => (
                                        <a key={idx} href={doc.url} target="_blank" rel="noreferrer" style={{
                                            textDecoration: 'none',
                                            padding: '16px 20px',
                                            backgroundColor: '#fff',
                                            border: '1.5px solid #F0F0F0',
                                            borderRadius: '16px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '12px',
                                            transition: 'all 0.2s'
                                        }} onMouseOver={(e) => e.currentTarget.style.borderColor = '#C0722C'}>
                                            <div style={{ background: '#FDF8F3', padding: '10px', borderRadius: '12px', color: '#C0722C' }}>
                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>
                                            </div>
                                            <div>
                                                <div style={{ fontSize: '14px', fontWeight: '700', color: '#2C1A10' }}>{doc.title}</div>
                                                <div style={{ fontSize: '11px', color: '#888' }}>Certified Document Reference</div>
                                            </div>
                                        </a>
                                    ))}
                                </div>
                            </div>
                        )}

                        {selectedAgenda.bills && selectedAgenda.bills.length > 0 && (
                            <div style={{ marginBottom: '30px' }}>
                                <div style={{ fontSize: '11px', fontWeight: '800', color: '#888', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '15px' }}>Linked Legislative Bills</div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '15px' }}>
                                    {selectedAgenda.bills.map((bill, idx) => (
                                        <div key={idx} style={{
                                            padding: '25px',
                                            backgroundColor: '#fff',
                                            border: '1.5px solid #F0F0F0',
                                            borderRadius: '24px',
                                            boxShadow: '0 4px 20px rgba(0,0,0,0.02)',
                                            position: 'relative',
                                            overflow: 'hidden'
                                        }}>
                                            <div style={{
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                width: '4px',
                                                height: '100%',
                                                backgroundColor: '#C0722C'
                                            }}></div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '15px' }}>
                                                <div>
                                                    <div style={{ fontSize: '10px', fontWeight: '900', color: '#C0722C', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '5px' }}>
                                                        {bill.type} Bill
                                                    </div>
                                                    <div style={{ fontSize: '19px', fontWeight: '800', color: '#2C1A10', lineHeight: '1.3' }}>{bill.title}</div>
                                                </div>
                                                <div style={{
                                                    padding: '5px 12px',
                                                    backgroundColor: '#E3F2FD',
                                                    color: '#1565C0',
                                                    borderRadius: '10px',
                                                    fontSize: '11px',
                                                    fontWeight: '800',
                                                    border: '1px solid rgba(21, 101, 192, 0.1)'
                                                }}>
                                                    Version {bill.version?.toFixed(1) || '1.0'}
                                                </div>
                                            </div>

                                            <div style={{ fontSize: '14px', color: '#555', lineHeight: '1.6', marginBottom: '22px', whiteSpace: 'pre-line' }}>
                                                {bill.description}
                                            </div>

                                            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                                                <a href={bill.documentUrl} target="_blank" rel="noreferrer" style={{
                                                    flex: 1,
                                                    textDecoration: 'none',
                                                    padding: '14px 20px',
                                                    backgroundColor: '#2C1A10',
                                                    borderRadius: '14px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: '10px',
                                                    color: 'white',
                                                    fontWeight: '700',
                                                    fontSize: '13px',
                                                    transition: 'all 0.3s ease',
                                                    boxShadow: '0 5px 15px rgba(44, 26, 16, 0.15)'
                                                }}
                                                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#C0722C'}
                                                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#2C1A10'}>
                                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>
                                                    View Bill Certificate
                                                </a>
                                                <div style={{
                                                    padding: '12px 16px',
                                                    backgroundColor: '#FDF8F3',
                                                    borderRadius: '12px',
                                                    color: '#C0722C',
                                                    fontSize: '12px',
                                                    fontWeight: '700',
                                                    border: '1px solid rgba(192, 114, 44, 0.1)'
                                                }}>
                                                    {bill.language || 'English'}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {selectedAgenda.status === 'SUBMITTED' ? (
                            <div style={{ animation: 'fadeIn 0.5s ease' }}>
                                <div style={{ marginBottom: '25px' }}>
                                    <label style={{ display: 'block', fontSize: '11px', fontWeight: '800', color: '#888', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '12px' }}>Reviewer Remarks (Mandatory for return)</label>
                                    <textarea
                                        value={remark}
                                        onChange={(e) => setRemark(e.target.value)}
                                        placeholder="Enter observations, clarifications, or missing parameters..."
                                        style={{
                                            width: '100%',
                                            height: '120px',
                                            padding: '18px',
                                            borderRadius: '16px',
                                            border: '1.5px solid #E0E0E0',
                                            fontFamily: 'inherit',
                                            fontSize: '15px',
                                            resize: 'none',
                                            backgroundColor: '#FAFAFA',
                                            boxSizing: 'border-box'
                                        }}
                                    />
                                </div>

                                <div style={{ display: 'flex', gap: '15px' }}>
                                    <button
                                        onClick={() => handleAction('RETURN')}
                                        style={{
                                            flex: 1, padding: '18px', backgroundColor: '#fff', color: '#D32F2F', border: '1.5px solid #D32F2F', borderRadius: '16px', fontWeight: '800', cursor: 'pointer', transition: 'all 0.3s'
                                        }}
                                        onMouseOver={(e) => e.target.style.backgroundColor = '#FFF1F1'}
                                        onMouseOut={(e) => e.target.style.backgroundColor = '#fff'}
                                    >
                                        Return to Minister
                                    </button>
                                    <button
                                        onClick={() => handleAction('FORWARD')}
                                        style={{
                                            flex: 1, padding: '18px', backgroundColor: '#2C1A10', color: 'white', border: 'none', borderRadius: '16px', fontWeight: '800', cursor: 'pointer', transition: 'all 0.3s', boxShadow: '0 8px 15px rgba(0,0,0,0.1)'
                                        }}
                                        onMouseOver={(e) => e.target.style.backgroundColor = '#C0722C'}
                                        onMouseOut={(e) => e.target.style.backgroundColor = '#2C1A10'}
                                    >
                                        Forward to Speaker
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div style={{ padding: '30px', backgroundColor: '#F9F9F9', borderRadius: '24px', border: '1.5px dashed #E0E0E0', textAlign: 'center' }}>
                                <div style={{ fontSize: '12px', fontWeight: '800', color: '#999', textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '5px' }}>Protocol Status</div>
                                <div style={{ fontSize: '20px', fontWeight: '800', color: getStatusStyles(selectedAgenda.status).text }}>Workflow Settled</div>
                                <div style={{ fontSize: '14px', color: '#777', marginTop: '8px' }}>This agenda has been processed and and is currently in <strong style={{ color: '#2C1A10' }}>{selectedAgenda.status.replace(/_/g, ' ')}</strong> state.</div>

                                {selectedAgenda.remarks && selectedAgenda.remarks.length > 0 && (
                                    <div style={{ marginTop: '25px', padding: '20px', backgroundColor: '#fff', borderRadius: '16px', textAlign: 'left', border: '1px solid #F0F0F0' }}>
                                        <div style={{ fontSize: '10px', fontWeight: '900', color: '#C0722C', textTransform: 'uppercase', marginBottom: '10px' }}>Archived Remark</div>
                                        <div style={{ fontStyle: 'italic', color: '#555', lineHeight: '1.6', fontSize: '14px' }}>"{selectedAgenda.remarks[selectedAgenda.remarks.length - 1].text}"</div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </SlidingDrawer>
        </div>
    );
};

export default AgendaManager;
